<template>
  <div class="main-container">
    <SidebarNav @update:sidebarNavCollapsed="sidebarNavCollapsed = $event" />
    <SearchBar :sidebar-nav-collapsed="sidebarNavCollapsed" />
    <div
      class="reports-container"
      :class="{['reports-container-sidebar-collapse']: sidebarNavCollapsed}"
    >
      <div class="reports-sub-container">
        <div class="table-header">
          {{ $t('invoices.title') }}
        </div>

        <div class="table-header-description">
          {{ $t('invoices.description') }}
        </div>

        <ReportTableFilterOption
          :disabled="loading.all"
          table-label="Invoices"
          v-bind="tableFilterOptionConfig"
          @childApplyFilter="applyFilter"
          @childClearFilter="applyFilter"
          ref="tableFilterOptionComponent"
          v-if="anyInvoiceExists"
          :date-list="invoicesDateList"
        />
        <div class="summary-container" v-if="!loading.all">
          <div class="summary-header" v-if="anyInvoiceExists">
            <div class="summary-title">
              {{ $t('invoices.heading', {month: getSummaryMonth}) }}
            </div>
            <div style="display: flex; gap: 8px;">
              <div v-if="currentInvoicePdfUrl">
                <c-button
                  variant="primary-outline"
                  :disabled="loading.all"
                  :loading="loading.invoice"
                  @click="openPdf"
                  class="primary-outline-btn"
                  style="display: flex; align-items: center;"
                >
                  <DownloadsIcon />
                  {{ $t('invoices.downloadPDF') }}
                </c-button>
              </div>
              <c-button
                variant="primary-outline"
                :disabled="loading.all"
                :loading="loading.invoice"
                @click="downloadCsv"
                class="primary-outline-btn"
                style="display: flex; align-items: center;"
              >
                <DownloadsIcon />
                {{ $t('invoices.downloadCSV') }}
              </c-button>
            </div>
          </div>

          <div class="summary-table" v-if="anyInvoiceExists">
            <c-table :variant="'stroke'">
              <c-tr v-for="summaryMetric in summaryMetrics" :key="summaryMetric[0]">
                <c-th class="title-cell">
                  {{ $t('invoices.summary.' + summaryMetric[0]) }}
                </c-th>
                <c-th class="value-cell" v-if="summaryMetric[0] === 'total_fees'">
                  <b> {{ summaryMetric[1] || '' }} </b>
                </c-th>
                <c-th class="value-cell" v-else>
                  {{ summaryMetric[1] || '' }}
                </c-th>
              </c-tr>
            </c-table>
          </div>
          <div class="empty-container" v-else>
            <EmptyPlaceholder
              :heading-tag="$t('invoices.noInvoicesYetTitle')"
              :description-tag="$t('invoices.noInvoicesYetDesc')"
              :clear-filter-flag="false"
            >
              <div slot="icon">
                <FolderIcon class="empty-placeholder-icon" />
              </div>
            </EmptyPlaceholder>
          </div>
        </div>
        <div class="loading-message" v-else>
          <div class="summary-table">
            <c-table variant="stroke">
              <c-tr v-for="index in 8" :key="index">
                <c-th>
                  <div class="line loading-shimmer"></div>
                </c-th>
                <c-th>
                  <div class="value-shimmer">
                    <div class="line loading-shimmer"></div>
                  </div>
                </c-th>
              </c-tr>
            </c-table>
          </div>
        </div>
        <PortalFooter />
      </div>
    </div>
  </div>
</template>

<script>
import SidebarNav from "@/components/SidebarNav.vue";
import SearchBar from "@/components/SearchBar.vue";
import {portalCheckMixin} from "@/mixin/portalPageCheck";
import "@/app/assets/style/reports.scss"
import {buildMonthYearObjForReport} from "@/app/utils/common/functions/dateUtils";
import Formatter from "@/app/utils/common/functions/formatter";
import merchantInvoice from "@/api/merchantInvoices";
import {mapGetters} from "vuex";
import ReportTableFilterOption from "@/components/common/ReportTableFilterOption";
//import ToolTip from "@/components/common/ToolTip.vue";
import DownloadsIcon from "@/components/icons/DownloadsIcon.vue";
import PortalFooter from "@/components/portal/PortalFooter.vue";
import EmptyPlaceholder from "@/components/transactions/EmptyPlaceholder.vue";
import FolderIcon from "@/components/icons/FolderIcon.vue";
import {MerchantInvoiceItemType} from "@/api/paymentApi";
import {downloadObjectAsCSV} from "@/app/utils/common/functions/fileUtils";
import {openUrlInNewTab} from "@/app/utils/common/functions/url-helper";

export default {
  name: 'MerchantInvoiceView',
  components: {
    //ToolTip,
    DownloadsIcon,
    SearchBar, SidebarNav, ReportTableFilterOption, PortalFooter, EmptyPlaceholder, FolderIcon
  },
  mixins: [portalCheckMixin],
  data() {
    return {
      currentInvoice: null,
      currentInvoicePdfUrl: null,
      invoices: null,
      sidebarNavCollapsed: false,
      loading: {
        all: false,
        invoice: false,
      },
      tableFilterOptionConfig: {},
      anyInvoiceExists: false
    };
  },
  computed: {
    ...mapGetters('app', ['getMerchantId', 'getMerchantCreatedAt', 'isAdminMode', 'getMerchantCurrency']),
    getSummaryMonth() {
      return this.currentInvoice ? Formatter.format({
        type: 'zonedDate',
        data: this.currentInvoice.start_date.toString(),
        format: 'MMM yyyy'
      }) : '';
    },
    summaryMetrics() {
      let metrics = [];

      if (!this.currentInvoice) {
        return metrics;
      }

      //invoice number
      metrics.push(['invoice_number', this.currentInvoice.invoice_number || '']);

      //invoice status
      metrics.push(['invoice_status', this.$t(`invoices.status.${this.currentInvoice.status}`) || '']);

      //invoice generation date
      metrics.push(['generation_date', this.getInvoiceGenerationDate()]);

      //invoice period
      metrics.push(['period', this.getInvoicePeriod()]);

      //invoice currency
      metrics.push(['currency', this.currentInvoice.currency]);

      //gross volume
      metrics.push(['gross_volume', this.getGrossVolume(true)]);

      //total fees
      metrics.push(['total_fees', Formatter.format({
        type: "currency",
        data: this.currentInvoice.amount,
        currencyCode: this.currentInvoice.currency
      })]);

      //amount due
      metrics.push(['amount_due', Formatter.format({
        type: "currency",
        data: this.currentInvoice.amount_due,
        currencyCode: this.currentInvoice.currency
      })]);
      return metrics;
    },
    invoicesDateList() {
      //return descending order sorted invoice date list
      return this.invoices && this.invoices.length > 0 && this.invoices.map((invoice) => {
        return new Date(invoice.start_date);
      }).sort((a, b) => b - a);
    },
  },
  mounted() {
    this.initialize();
  },
  methods: {
    async initialize() {
      this.loading.all = true;
      try {
        let invoiceList = await merchantInvoice.listMerchantInvoices(this.getMerchantId);
        if (invoiceList && invoiceList.length > 0) {
          this.invoices = invoiceList
          //sort invoices by id desc order and take first
          this.currentInvoice = this.invoices.sort((a, b) => b.id.localeCompare(a.id))[0];
          await this.loadCurrentInvoicePdfUrl();
          this.anyInvoiceExists = true;
        } else {
          this.anyInvoiceExists = false;
          this.$danger(this.$t('invoices.noInvoicesYetTitle'), {dismissable: true, timeout: 4000});
        }
      } catch (e) {
        this.anyInvoiceExists = false;
        this.$danger(this.$t('invoices.noInvoicesYetTitle'), {dismissable: true, timeout: 4000});
      } finally {
        this.loading.all = false;
      }
    },
    async loadCurrentInvoicePdfUrl() {
      if (this.currentInvoice) {
        try {
          this.currentInvoicePdfUrl = (await merchantInvoice
              .downloadMerchantInvoicePdf(this.getMerchantId, this.currentInvoice.id))?.url ?? null;
        } catch {
          this.currentInvoicePdfUrl = null;
        }
      }
    },
    downloadCsv() {
      let csvData = {};

      csvData['Total Processed Volume for the period'] = this.getGrossVolume(false);

      csvData['Total Transaction Count'] = this.getTotalTransactionsCount();

      csvData['Invoice Number'] = this.currentInvoice.invoice_number || '';

      csvData['Invoice Generation Date'] = this.getInvoiceGenerationDate();

      csvData['Company Code'] = this.currentInvoice.summary?.company_code || '';

      csvData['Invoice Period'] = this.getInvoicePeriod();

      csvData['Invoice Currency'] = this.currentInvoice.currency;

      csvData['Chargebee Fees'] = this.itemValue(MerchantInvoiceItemType.CHARGEBEEFEE);

      csvData['Interchange Fees'] = this.itemValue(MerchantInvoiceItemType.INTERCHANGEFEE);

      csvData['Scheme Fees'] = this.itemValue(MerchantInvoiceItemType.SCHEMEFEE);

      csvData['3DS Fees'] = this.itemValue(MerchantInvoiceItemType.THREEDSFEE);

      csvData['Forex Fees'] = this.itemValue(MerchantInvoiceItemType.FOREXFEE);

      //if there are some blended txns present in that month
      this.addFeesToCsvIfPresent(csvData, MerchantInvoiceItemType.CROSSBORDERFEE, 'Cross Border Fees');

      //for older invoices
      this.addFeesToCsvIfPresent(csvData, MerchantInvoiceItemType.PAYMENTGATEWAYFEE, 'Payment Gateway Processing Fees');
      
      //for older invoices
      this.addFeesToCsvIfPresent(csvData, MerchantInvoiceItemType.ACQUIRERMARKUPFEE, 'Acquirer Markup');

      csvData['Non-Transactional Fees: Visa System Integrity Fee'] = this.itemValue(MerchantInvoiceItemType.VISASYSTEMINTEGRITYFEE);

      csvData['Non-Transactional Fees: Visa Stop Payment Service Fee'] = this.itemValue(MerchantInvoiceItemType.VISASTOPPAYMENTSERVICEFEE);

      csvData['Non-Transactional Fees: Visa Digital Credential Updater Fee'] = this.itemValue(MerchantInvoiceItemType.VISADIGITALCREDENTIALUPDATERFEE);

      csvData['Non-Transactional Fees: Visa Fixed Acquirer Network Fee'] = this.itemValue(MerchantInvoiceItemType.VISAFIXEDACQUIRERNETWORKFEE);

      csvData['Non-Transactional Fees: Visa Token Service Fee'] = this.itemValue(MerchantInvoiceItemType.VISATOKENSERVICEFEE);

      csvData['Non-Transactional Fees: Visa 3DS Fee'] = this.itemValue(MerchantInvoiceItemType.VISATHREEDSFEE);

      csvData['Non-Transactional Fees: Mastercard TPE Excessive Authorizations Fee'] = this.itemValue(MerchantInvoiceItemType.MASTERCARDTPEEXCESSIVEAUTHORIZATIONSFEE);

      csvData['Non-Transactional Fees: Mastercard TPE Merchant Advice Code Fee'] = this.itemValue(MerchantInvoiceItemType.MASTERCARDTPEMERCHANTADVICECODEFEE);

      csvData['Non-Transactional Fees: Mastercard Merchant Location Fee'] = this.itemValue(MerchantInvoiceItemType.MASTERCARDMERCHANTLOCATIONFEE);

      csvData['Non-Transactional Fees: Mastercard 3DS Fee'] = this.itemValue(MerchantInvoiceItemType.MASTERCARDTHREEDSFEE);

      csvData['Non-Transactional Fees: Authorisation Scheme fee Authorised'] = this.itemValue(MerchantInvoiceItemType.AUTHORIZATIONSCHEMEFEEAUTHORISED);

      csvData['Non-Transactional Fees: Authorisation Scheme fee Cancelled'] = this.itemValue(MerchantInvoiceItemType.AUTHORIZATIONSCHEMEFEECANCELLED);

      csvData['Non-Transactional Fees: Authorisation Scheme fee - Refused'] = this.itemValue(MerchantInvoiceItemType.AUTHORIZATIONSCHEMEFEEREFUSED);

      csvData['Non-Transactional Fees: Authorisation Scheme fee - Refused retries'] = this.itemValue(MerchantInvoiceItemType.AUTHORIZATIONSCHEMEFEEREFUSEDRETRIES);

      csvData['Total Non-Transactional Fees'] = this.currencyFormat(this.currentInvoice.summary?.total_non_transactional_fees);

      csvData['Invoice Total excluding taxes'] = this.currencyFormat(this.currentInvoice.amount);

      csvData['Taxes and Fees'] = "0.00";

      csvData['Invoice Total including taxes'] = this.currencyFormat(this.currentInvoice.amount);

      csvData['Already deducted from settlement'] = this.currencyFormat(this.currentInvoice.summary?.total_transactional_fees);

      csvData['Amount due (auto deduction)'] = this.currencyFormat(this.currentInvoice.amount_due);

      csvData['Amount due has been deducted from Payout Id'] = this.getPayoutId();

      csvData['No money needs to be transferred'] = '';

      let fileName = this.currentInvoice.invoice_number;

      if (!fileName) {
        fileName = 'invoice_' + Formatter.format({
          type: 'zonedDate',
          data: this.currentInvoice.start_date.toString(),
          format: 'MMM_yyyy'
        }).toLowerCase();
      }

      downloadObjectAsCSV(csvData, fileName + '.csv')
    },
    async applyFilter(filter) {
      this.loading.all = true;
      try {
        this.currentInvoice = this.invoices
            .filter((invoice) => buildMonthYearObjForReport(new Date(invoice.start_date), false).key === filter.currentMonthFilter)[0];
        await this.loadCurrentInvoicePdfUrl();
      } finally {
        this.loading.all = false;
      }
    },
    getInvoiceGenerationDate() {
      return Formatter.format({
        type: 'zonedDateWithTime',
        data: this.currentInvoice?.created_at.toString()
      });
    },
    getItem(itemType) {
      return this.currentInvoice.invoice_items?.filter(item => item.item_type === itemType)[0];
    },
    getGrossVolume(includeCurrencyCode) {
      return this.currencyFormat(this.currentInvoice.summary?.gross_volume, includeCurrencyCode? this.currentInvoice.currency : undefined);
    },
    getTotalTransactionsCount() {
      return this.currentInvoice.summary?.total_tranasctions || '';
    },
    getInvoicePeriod() {
      let start = Formatter.format({
        type: 'zonedDate',
        data: this.currentInvoice.start_date.toString(),
        format: 'MMM dd'
      })
      let end = Formatter.format({
        type: 'zonedDate',
        data: this.currentInvoice.end_date.toString(),
        format: 'MMM dd'
      })
      return `${start} - ${end}`;
    },
    getPayoutId() {
      return this.currentInvoice.collected_payments?.[0]?.deposit_id || ''
    },
    itemValue(itemType) {
      let item = this.getItem(itemType);
      return item ? this.currencyFormat(item.amount) : 0 ;
    },
    addFeesToCsvIfPresent(csvData, itemType, itemName) {
      let value = this.itemValue(itemType);
      if (value) {
        csvData[itemName] = value;
      }
    },
    currencyFormat(value, currencyCode) {
      if (!value) {
        return '';
      }
      let config = {data: value};
      if (!!currencyCode) {
        config.currencyCode = currencyCode;
      }
      return Formatter.format({
        ...config,
        type: "currency"
      }) || '';
    },
    openPdf() {
      this.currentInvoicePdfUrl && openUrlInNewTab(this.currentInvoicePdfUrl);
    }
  }
}

</script>